/**
 * nativeShell.ts — Shell visual para app nativa (Capacitor APK / iOS).
 *
 * QUÉ HACE:
 *  - Marca `<html>` con `native-app` para activar CSS de safe area solo en nativo
 *  - Sincroniza color de la status bar con tema claro/oscuro
 *
 * QUÉ EXPORTA: initNativeAppShell, syncNativeStatusBar, clases reutilizables (SAFE_AREA)
 *
 * SAFE_AREA: usar en layout/modales; el CSS suma inset + content-gap (ver native-safe-area.css).
 *
 * QUÉ NO DEBE HACER:
 *  - No contiene JSX ni lógica de negocio
 *  - No sustituye a @/lib/platform (usa isNative desde ahí)
 */

import {
  isAndroid,
  isDesktop,
  isIOS,
  isNative,
  platform,
} from "@/lib/platform";

/** Clase en `<html>`; los estilos en native-safe-area.css dependen de ella. */
export const NATIVE_APP_CLASS = "native-app";
/** Clase en `<html>` para entorno Tauri desktop. */
export const DESKTOP_APP_CLASS = "desktop-app";

/** Clases semánticas del shell — usar en layout/modales en lugar de env() inline. */
export const SAFE_AREA = {
  sidebar: "app-sidebar",
  sidebarFooter: "app-sidebar__footer",
  mobileHeader: "app-mobile-header",
  mobileBottomNav: "app-mobile-bottom-nav",
  mainScroll: "app-main-scroll",
  mainScrollMobileNav: "app-main-scroll--with-mobile-nav",
  modalOverlay: "app-modal-overlay",
  fixedBottom: "app-fixed-bottom",
  fixedBottomMargin: "app-fixed-bottom-margin",
  fixedBottomMobileNav: "app-fixed-bottom--above-mobile-nav",
  mobileSheet: "app-mobile-sheet",
  fabBottom: "app-fab-safe-bottom",
  fabBottomLg: "app-fab-safe-bottom-lg",
  fabBottomXl: "app-fab-safe-bottom-xl",
  fabAbovePageFab: "app-fab-above-page-fab",
  fabAboveCart: "app-fab-above-cart",
  tiendaPublicShell: "app-tienda-public-shell",
  authShell: "app-auth-shell",
  loadErrorApp: "app-load-error-app",
  toaster: "app-toaster",
  drawerPanel: "app-drawer-panel",
  copilotOverlay: "app-copilot-overlay",
} as const;

/** Activa variables y reglas CSS en nativo (Capacitor) o desktop (Tauri). */
export function initNativeAppShell(): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  root.dataset.platform = platform;

  if (isDesktop) {
    root.classList.add(DESKTOP_APP_CLASS);
    // En desktop no hay safe-area de dispositivo, pero sí titlebar en Windows
    root.classList.add("native-desktop");
    return;
  }

  if (!isNative) return;
  root.classList.add(NATIVE_APP_CLASS);
  if (isIOS) root.classList.add("native-ios");
  if (isAndroid) root.classList.add("native-android");
}

/** Status bar sólida (no overlay) alineada al fondo del sidebar/header. */
export async function syncNativeStatusBar(): Promise<void> {
  if (!isNative) return;

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    const dark = document.documentElement.classList.contains("dark");
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light });
    await StatusBar.setBackgroundColor({
      color: dark ? "#0f172a" : "#ffffff",
    });
    if (isAndroid) {
      await syncAndroidNavigationBarBlack();
    }
  } catch {
    /* no crítico */
  }
}

async function syncAndroidNavigationBarBlack(): Promise<void> {
  try {
    const { registerPlugin } = await import("@capacitor/core");
    const AltenotShell = registerPlugin<{
      applyNavigationBar(): Promise<void>;
    }>("AltenotShell");
    await AltenotShell.applyNavigationBar();
  } catch {
    /* APK sin plugin; MainActivity también aplica en onResume */
  }
}
